import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, FileText, Clock, CheckCircle,
  CalendarDays, TrendingUp, ArrowUpRight, ShieldCheck
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatCard from "@/components/admin/StatCard";
import StatusBadge from "@/components/admin/StatusBadge";
import PageHeader from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/admin/dashboard")
      .then(r => setData(r.data))
      .catch(e => { toast.error(errMsg(e)); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader
          title="Dashboard"
          subtitle="Overview of your Zenstrust platform"
          actions={
            <div className="flex items-center gap-2">
              {user?.role === "super_admin" && (
                <Button
                  size="sm"
                  onClick={() => navigate("/admin/management")}
                  className="bg-[hsl(var(--zt-teal))] text-white hover:bg-[hsl(var(--zt-teal))]/90 font-heading uppercase tracking-wider text-xs rounded-full h-9 px-5"
                >
                  <ShieldCheck size={13} className="mr-1.5" /> Admin Management
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => navigate("/admin/consultations")}
              className="bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 font-heading uppercase tracking-wider text-xs rounded-full h-9 px-5"
            >
              View All Consultations <ArrowUpRight size={13} className="ml-1.5" />
            </Button>
            </div>
          }
        />

        {loading ? (
          <DashboardSkeleton />
        ) : error ? (
          <div className="bg-white rounded-2xl border border-red-100 p-16 text-center shadow-sm">
            <p className="text-sm text-red-500">Failed to load dashboard data. Please refresh.</p>
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Users"        value={data.stats.total_users}           icon={Users}       color="teal" />
              <StatCard label="Advisors"           value={data.stats.total_advisors}        icon={UserCheck}   color="gold" />
              <StatCard label="Consultations"      value={data.stats.total_consultations}   icon={FileText}    color="teal" />
              <StatCard label="Pending"            value={data.stats.pending_consultations} icon={Clock}       color="orange" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Completed"          value={data.stats.completed_consultations} icon={CheckCircle}  color="green" />
              <StatCard label="Assigned"           value={data.stats.assigned_consultations}  icon={FileText}     color="teal" />
              <StatCard label="Today's Apts"       value={data.stats.todays_appointments}     icon={CalendarDays} color="gold" />
              <StatCard label="Upcoming Apts"      value={data.stats.upcoming_appointments}   icon={TrendingUp}   color="teal" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-5">Monthly Consultations</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.monthly_consultations}>
                    <defs>
                      <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#101828" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#101828" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0ece7" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "Outfit" }} />
                    <YAxis tick={{ fontSize: 10, fontFamily: "Outfit" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, fontFamily: "Outfit", borderRadius: 8 }} />
                    <Area type="monotone" dataKey="consultations" stroke="#101828" strokeWidth={2} fill="url(#cGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-5">Monthly New Users</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.monthly_users}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0ece7" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "Outfit" }} />
                    <YAxis tick={{ fontSize: 10, fontFamily: "Outfit" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, fontFamily: "Outfit", borderRadius: 8 }} />
                    <Bar dataKey="users" fill="#4F63F0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent rows */}
            <div className="grid lg:grid-cols-2 gap-6">
              <RecentTable
                title="Recent Users"
                onViewAll={() => navigate("/admin/users")}
                headers={["Name / Email", "Country", "Role", "Joined"]}
                rows={data.recent_users.map(u => [
                  <div>
                    <p className="font-semibold text-sm text-[hsl(var(--zt-teal))]">{u.name || "—"}</p>
                    <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{u.email}</p>
                  </div>,
                  <span className="text-sm text-[hsl(var(--zt-charcoal))]/70">{u.country || "—"}</span>,
                  <StatusBadge status={u.role} />,
                  <span className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtDate(u.created_at)}</span>,
                ])}
              />

              <RecentTable
                title="Recent Consultations"
                onViewAll={() => navigate("/admin/consultations")}
                headers={["Subject", "User", "Advisor", "Status"]}
                rows={data.recent_consultations.map(c => [
                  <p className="font-semibold text-sm text-[hsl(var(--zt-teal))] line-clamp-1">{c.subject}</p>,
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/70">{c.user_name}</p>,
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{c.advisor_name || "Unassigned"}</p>,
                  <StatusBadge status={c.status} />,
                ])}
              />
            </div>
          </div>
        ) : null}
      </AdminLayout>
    </AdminGuard>
  );
}

function RecentTable({ title, headers, rows, onViewAll }) {
  return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
      <div className="px-6 py-4 flex items-center justify-between border-b border-[hsl(var(--zt-beige-deep))]">
        <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">{title}</h3>
        <button onClick={onViewAll} className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-gold))] hover:text-[hsl(var(--zt-gold))]/80 flex items-center gap-1">
          View all <ArrowUpRight size={11} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[hsl(var(--zt-beige-deep))]">
              {headers.map(h => (
                <th key={h} className="px-4 py-3 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="px-4 py-8 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">No data</td></tr>
            ) : rows.map((cells, i) => (
              <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/40 transition-colors">
                {cells.map((cell, j) => (
                  <td key={j} className="px-4 py-3">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 h-24" />
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl h-64 border border-[hsl(var(--zt-beige-deep))]" />
        <div className="bg-white rounded-2xl h-64 border border-[hsl(var(--zt-beige-deep))]" />
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
